import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconClose } from './icons.js';
import { Button } from './primitives.js';

interface ModalProps {
  title: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}

/**
 * Diálogo em portal — escapa do `overflow` do conteúdo e cobre a tela inteira.
 *
 * Esc fecha, clique no fundo fecha, e o foco vai para dentro na montagem. O
 * portal vai para o `body` porque o `.content` tem `overflow-y: auto` e prenderia
 * o overlay se ele fosse filho.
 */
export function Modal({ title, sub, onClose, children, footer, wide }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', aoTeclar);
    // Foca o primeiro elemento focável do diálogo, sem roubar de um input já mirado.
    ref.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onClose]);

  return createPortal(
    <div className="overlay" onMouseDown={onClose}>
      <div
        ref={ref}
        className={`modal${wide ? ' modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal__head">
          <div>
            <div className="modal__title">{title}</div>
            {sub && <div className="modal__sub">{sub}</div>}
          </div>
          <button type="button" className="modal__x" aria-label="Fechar" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmProps {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/** Confirmação simples para ações destrutivas (desativar régua, remover etapa). */
export function Confirm({
  title,
  message,
  confirmLabel = 'Confirmar',
  danger,
  busy,
  onConfirm,
  onClose,
}: ConfirmProps) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="muted" style={{ lineHeight: 1.6 }}>
        {message}
      </p>
    </Modal>
  );
}
